import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { ScrollableWithBackToTopHandle } from '@app/core/ui/ScrollableWithBackToTop';
import { Box } from '@mui/material';
import { useRef } from 'react';
import { defineMessage, useIntl } from 'react-intl';

import { usePlaybackCurrentTs } from '@app/core/playback/hooks/usePlayback';
import { LiveTopicsPanelCategoryToolbar } from './category-toolbar/LiveTopicsPanelCategoryToolbar';
import { useLiveTopicsCategoryFilter } from './category-toolbar/useLiveTopicsCategoryFilter';
import { LiveTopicsDetailView } from './detail-view/LiveTopicsDetailView';
import { isTopicActiveAt } from './live-topics-panel-utils';
import { LiveTopicsPanelHeader } from './header/LiveTopicsPanelHeader';
import { useLatestLiveTopics } from './hooks/useLatestLiveTopics';
import { useLiveTopicsNewArrivals } from './hooks/useLiveTopicsNewArrivals';
import { useLiveTopicsScrollLock } from './hooks/useLiveTopicsScrollLock';
import { useLiveTopicsSelection } from './hooks/useLiveTopicsSelection';
import { LiveTopicsPanelListView } from './list-view/LiveTopicsPanelListView';

const panelTitleMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.title',
  defaultMessage: 'Live Topics',
});

const newTopicsPillMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.newTopics',
  defaultMessage: '{count, plural, one {# tema nuevo} other {# temas nuevos}}',
});

const noTopicsMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.noTopicsForRange',
  defaultMessage: 'No hay temas para el rango seleccionado.',
});

/** Shared layout for panel root: full-height column with hidden overflow. */
const panelRootSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
} as const;

/**
 * Live Topics drawer panel: list view (divider-separated items, scroll-lock, "N Temas Nuevos" pill)
 * and detail view (popover-like content + Previous/Next). Uses latest topics API with polling.
 */
export function LiveTopicsPanel() {
  const { closeDrawer } = usePageDrawer();
  const { $t } = useIntl();

  const currentTs = usePlaybackCurrentTs();

  const { topics, isSuccess, isLoading } = useLatestLiveTopics();
  const { filteredTopics, selectedCategories, setSelectedCategories, categoryOptions } =
    useLiveTopicsCategoryFilter(topics);
  const { newTopicsCount, setNewTopicsCount } = useLiveTopicsScrollLock();
  const scrollableRef = useRef<ScrollableWithBackToTopHandle>(null);

  const { selectedTopicId, setSelectedTopicId, selectedTopic, isDetailView, handleBack } =
    useLiveTopicsSelection(topics);

  const { newlyArrivedTopicIds, viewedInDetailTopicIds } = useLiveTopicsNewArrivals(
    topics,
    isSuccess,
    selectedCategories,
    selectedTopicId,
    scrollableRef,
    setNewTopicsCount,
  );

  const header = (
    <LiveTopicsPanelHeader
      title={$t(panelTitleMessage)}
      isDetailView={isDetailView}
      onBack={handleBack}
      onClose={closeDrawer}
    />
  );

  /** List and detail are always in the tree; we hide the inactive one with CSS so the list never unmounts when opening detail (avoids flash on back). */
  const showList = !isDetailView;
  const showDetail = isDetailView && selectedTopic != null;

  return (
    <Box sx={panelRootSx}>
      {header}
      <Box
        key="live-topics-list"
        sx={{
          display: showList ? 'flex' : 'none',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <LiveTopicsPanelCategoryToolbar
          categoryOptions={categoryOptions}
          selectedCategories={selectedCategories}
          onSelectedCategoriesChange={setSelectedCategories}
        />
        <LiveTopicsPanelListView
          scrollableRef={scrollableRef}
          onScrollToTop={() => setNewTopicsCount(0)}
          isLoading={isLoading}
          filteredTopics={filteredTopics}
          emptyMessage={$t(noTopicsMessage)}
          newlyArrivedTopicIds={newlyArrivedTopicIds}
          viewedInDetailTopicIds={viewedInDetailTopicIds}
          currentTs={currentTs}
          onTopicClick={setSelectedTopicId}
          newTopicsPillLabel={$t(newTopicsPillMessage, { count: newTopicsCount })}
          newTopicsCount={newTopicsCount}
        />
      </Box>
      {selectedTopic && (
        <Box
          key="live-topics-detail"
          sx={{
            display: showDetail ? 'flex' : 'none',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <LiveTopicsDetailView
            topic={selectedTopic}
            topics={topics}
            onSelectTopic={setSelectedTopicId}
            onClose={closeDrawer}
            isActive={isTopicActiveAt(selectedTopic, currentTs)}
          />
        </Box>
      )}
    </Box>
  );
}
